import type { WorkActionContext } from '@shared/types/work-action';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { DashboardChatPageContext } from '@shared/types/dashboard-chat';
import type {
  DashboardChatThreadInteraction,
} from '@shared/types/dashboard-chat';
import type { PrebuiltToolKey } from '@shared/types/prebuilt-tools';
import type { ThreadDecisionFocus } from '@shared/types/thread-work';

const COLLAPSED_STORAGE_KEY = 'domino.appAssistant.collapsed';
const CONVERSATION_STORAGE_KEY = 'domino.appAssistant.conversationId';
const WORK_FOCUS_STORAGE_KEY = 'domino.appAssistant.workFocus';

const userStorageKey = (key: string, userId: string): string => `${key}:${userId}`;

const currentThreadSubjectId = (pathname: string): string | null => {
  const match = pathname.match(/^\/threads\/([0-9a-f-]{36})(?:\/|$)/i);
  return match?.[1] ?? null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const nullableString = (value: unknown): string | null | undefined =>
  value === null || typeof value === 'string' ? value : undefined;

const normalizeStoredWorkFocus = (value: unknown): WorkActionContext | null => {
  if (!isRecord(value)) return null;
  const subjectKey = nullableString(value.subjectKey);
  const threadId = nullableString(value.threadId);
  const topicId = nullableString(value.topicId);
  const resultId = value.resultId === undefined || typeof value.resultId === 'string'
    ? value.resultId as string | undefined
    : undefined;
  const flowResultId = value.flowResultId === undefined || typeof value.flowResultId === 'string'
    ? value.flowResultId as string | undefined : undefined;
  const sourceRunId = nullableString(value.sourceRunId);
  const sourceActionId = nullableString(value.sourceActionId);
  const topicVersion = value.topicVersion === null ||
      (Number.isInteger(value.topicVersion) && Number(value.topicVersion) >= 1)
    ? value.topicVersion as number | null
    : undefined;
  if (
    typeof value.subjectId !== 'string' ||
    typeof value.subjectKind !== 'string' ||
    typeof value.subjectLabel !== 'string' ||
    typeof value.title !== 'string' ||
    subjectKey === undefined ||
    threadId === undefined ||
    topicId === undefined ||
    (resultId === undefined && value.resultId !== undefined) ||
    (flowResultId === undefined && value.flowResultId !== undefined) ||
    topicVersion === undefined ||
    sourceRunId === undefined ||
    sourceActionId === undefined
  ) return null;
  return {
    subjectId: value.subjectId,
    subjectKey,
    subjectKind: value.subjectKind,
    subjectLabel: value.subjectLabel,
    threadId,
    topicId,
    resultId,
    flowResultId,
    topicVersion,
    sourceRunId,
    sourceActionId,
    title: value.title,
    decisionFocus: null,
  };
};

const readStoredWorkFocus = (
  userId: string,
  subjectId: string | null,
): WorkActionContext | null => {
  if (typeof window === 'undefined' || !subjectId) return null;
  try {
    const stored = window.sessionStorage.getItem(userStorageKey(WORK_FOCUS_STORAGE_KEY, userId));
    const parsed: unknown = stored ? JSON.parse(stored) : null;
    if (!isRecord(parsed)) return null;
    const focus = normalizeStoredWorkFocus(parsed[subjectId]);
    return focus?.subjectId === subjectId ? focus : null;
  } catch {
    return null;
  }
};

const writeStoredWorkFocus = (userId: string, focus: WorkActionContext | null): void => {
  if (typeof window === 'undefined') return;
  const key = userStorageKey(WORK_FOCUS_STORAGE_KEY, userId);
  try {
    const parsed: unknown = JSON.parse(window.sessionStorage.getItem(key) ?? '{}');
    const stored = isRecord(parsed) ? parsed : {};
    if (focus) stored[focus.subjectId] = normalizeStoredWorkFocus(focus);
    window.sessionStorage.setItem(key, JSON.stringify(stored));
  } catch {
    // Persistence is a convenience; the server-side conversation remains authoritative.
  }
};

const removeStoredWorkFocus = (userId: string, subjectId: string): void => {
  if (typeof window === 'undefined') return;
  const key = userStorageKey(WORK_FOCUS_STORAGE_KEY, userId);
  try {
    const parsed: unknown = JSON.parse(window.sessionStorage.getItem(key) ?? '{}');
    if (!isRecord(parsed)) return;
    delete parsed[subjectId];
    window.sessionStorage.setItem(key, JSON.stringify(parsed));
  } catch {
    // The selection still clears in memory when session storage is unavailable.
  }
};

export interface AppAssistantContextSnapshot extends DashboardChatPageContext {
  surface: 'home' | 'tool' | 'forge';
  dashboardId?: string | null;
  toolKey?: PrebuiltToolKey | null;
  toolTitle?: string | null;
  executionId?: string | null;
  executionLabel?: string | null;
}

interface PendingAssistantCommand {
  id: string;
  text: string;
  threadInteraction?: DashboardChatThreadInteraction;
}

interface AppAssistantContextValue {
  workFocus: WorkActionContext | null;
  openWork: (context: WorkActionContext) => void;
  leaveWork: () => void;
  collapsed: boolean;
  activeConversationId: string | null;
  pageContext: AppAssistantContextSnapshot | null;
  decisionFocus: ThreadDecisionFocus | null;
  pendingCommand: PendingAssistantCommand | null;
  openAssistant: () => void;
  closeAssistant: () => void;
  toggleAssistant: () => void;
  setActiveConversationId: (conversationId: string | null) => void;
  setPageContext: (context: AppAssistantContextSnapshot | null) => void;
  pinDecisionFocus: (focus: ThreadDecisionFocus) => void;
  clearDecisionFocus: () => void;
  submitAssistantCommand: (
    text: string,
    options?: { threadInteraction?: DashboardChatThreadInteraction }
  ) => void;
  clearPendingCommand: (commandId: string) => void;
}

const AppAssistantContext = createContext<AppAssistantContextValue | null>(null);

const readStoredBoolean = (key: string, fallback: boolean): boolean => {
  if (typeof window === 'undefined') return fallback;
  const stored = window.localStorage.getItem(key);
  if (stored === 'true') return true;
  if (stored === 'false') return false;
  return fallback;
};

const readStoredString = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(key);
  return stored && stored.length > 0 ? stored : null;
};

export const AppAssistantProvider = ({
  children,
  userId = 'anonymous',
}: {
  children: ReactNode;
  userId?: string;
}) => {
  const location = useLocation();
  const routeSubjectId = currentThreadSubjectId(location.pathname);
  const [workFocus, setWorkFocus] = useState<WorkActionContext | null>(() =>
    readStoredWorkFocus(userId, routeSubjectId)
  );
  const [collapsed, setCollapsed] = useState(() =>
    readStoredBoolean(userStorageKey(COLLAPSED_STORAGE_KEY, userId), true)
  );
  const [activeConversationId, setActiveConversationIdState] = useState(() =>
    readStoredString(userStorageKey(CONVERSATION_STORAGE_KEY, userId))
  );
  const [pageContext, setPageContext] = useState<AppAssistantContextSnapshot | null>(
    null
  );
  const [decisionFocus, setDecisionFocus] = useState<ThreadDecisionFocus | null>(null);
  const [pendingCommand, setPendingCommand] = useState<PendingAssistantCommand | null>(
    null
  );
  const previousRouteSubjectIdRef = useRef(routeSubjectId);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const returnScrollTopRef = useRef<number | null>(null);

  const rememberReturnPosition = () => {
    returnFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    returnScrollTopRef.current = document.querySelector('main')?.scrollTop ?? null;
  };

  const restoreReturnPosition = () => {
    const element = returnFocusRef.current;
    const scrollTop = returnScrollTopRef.current;
    window.requestAnimationFrame(() => {
      if (scrollTop !== null) document.querySelector('main')?.scrollTo({ top: scrollTop });
      if (element?.isConnected) element.focus();
    });
  };

  useEffect(() => {
    if (previousRouteSubjectIdRef.current === routeSubjectId) return;
    previousRouteSubjectIdRef.current = routeSubjectId;
    if (!routeSubjectId) return;
    if (workFocus?.subjectId === routeSubjectId) return;
    const restored = readStoredWorkFocus(userId, routeSubjectId);
    setWorkFocus(restored);
    if (!restored && workFocus) {
      setCollapsed(true);
      window.localStorage.setItem(userStorageKey(COLLAPSED_STORAGE_KEY, userId), 'true');
    }
  }, [routeSubjectId, userId]);

  useEffect(() => {
    if (!workFocus?.flowResultId || workFocus.subjectId !== routeSubjectId) return;
    if (new URLSearchParams(location.search).get('flow') === workFocus.flowResultId) return;
    removeStoredWorkFocus(userId, workFocus.subjectId);
    setWorkFocus(null);
    setCollapsed(true);
  }, [routeSubjectId, location.search, workFocus?.flowResultId, userId]);

  useEffect(() => {
    const handleShortcut = (event: globalThis.KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;
      event.preventDefault();
      rememberReturnPosition();
      setCollapsed(false);
      window.localStorage.setItem(userStorageKey(COLLAPSED_STORAGE_KEY, userId), 'false');
    };

    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [userId]);

  const setActiveConversationId = (conversationId: string | null) => {
    setActiveConversationIdState(conversationId);
    if (conversationId) {
      window.localStorage.setItem(userStorageKey(CONVERSATION_STORAGE_KEY, userId), conversationId);
    } else {
      window.localStorage.removeItem(userStorageKey(CONVERSATION_STORAGE_KEY, userId));
    }
  };

  const value = useMemo<AppAssistantContextValue>(
    () => ({
      collapsed,
      workFocus,
      openWork: (context) => {
        rememberReturnPosition();
        writeStoredWorkFocus(userId, context);
        setWorkFocus(context);
        setCollapsed(false);
        window.localStorage.setItem(userStorageKey(COLLAPSED_STORAGE_KEY, userId), 'false');
      },
      leaveWork: () => {
        if (workFocus) removeStoredWorkFocus(userId, workFocus.subjectId);
        setWorkFocus(null);
      },
      activeConversationId,
      pageContext,
      decisionFocus,
      pendingCommand,
      openAssistant: () => {
        rememberReturnPosition();
        setCollapsed(false);
        window.localStorage.setItem(userStorageKey(COLLAPSED_STORAGE_KEY, userId), 'false');
      },
      closeAssistant: () => {
        setCollapsed(true);
        window.localStorage.setItem(userStorageKey(COLLAPSED_STORAGE_KEY, userId), 'true');
        restoreReturnPosition();
      },
      toggleAssistant: () => {
        setCollapsed((current) => {
          const next = !current;
          if (!next) rememberReturnPosition();
          window.localStorage.setItem(
            userStorageKey(COLLAPSED_STORAGE_KEY, userId),
            String(next)
          );
          if (next) restoreReturnPosition();
          return next;
        });
      },
      setActiveConversationId,
      setPageContext,
      pinDecisionFocus: setDecisionFocus,
      clearDecisionFocus: () => setDecisionFocus(null),
      submitAssistantCommand: (text, options) => {
        const trimmed = text.trim();
        if (!trimmed) return;
        rememberReturnPosition();
        setCollapsed(false);
        window.localStorage.setItem(userStorageKey(COLLAPSED_STORAGE_KEY, userId), 'false');
        setWorkFocus(null);
        setPendingCommand({
          id: crypto.randomUUID(),
          text: trimmed,
          ...(options?.threadInteraction
            ? { threadInteraction: options.threadInteraction }
            : {}),
        });
      },
      clearPendingCommand: (commandId: string) => {
        setPendingCommand((current) => (current?.id === commandId ? null : current));
      },
    }),
    [activeConversationId, collapsed, decisionFocus, pageContext, pendingCommand, userId, workFocus]
  );

  return (
    <AppAssistantContext.Provider value={value}>{children}</AppAssistantContext.Provider>
  );
};

export const useAppAssistant = (): AppAssistantContextValue => {
  const context = useContext(AppAssistantContext);
  if (!context) {
    throw new Error('useAppAssistant must be used inside AppAssistantProvider');
  }
  return context;
};

/** A page tells the side chat what it shows; outside the app shell (tests, standalone renders) it is a no-op. */
export const useRegisterAppAssistantContext = (
  context: AppAssistantContextSnapshot | null
) => {
  const setPageContext = useContext(AppAssistantContext)?.setPageContext;

  useEffect(() => {
    if (!setPageContext) return;
    setPageContext(context);
    return () => setPageContext(null);
  }, [context, setPageContext]);
};

export const useOptionalAppAssistant = () => useContext(AppAssistantContext);
